// frontend/src/hooks/games/useSnakeGame.ts

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GRID_SIZE,
  BASE_TICK_MS,
  FRUIT_EMOJIS,
  FRUIT_SCORE,
  SPECIAL_ITEMS,
  SPECIAL_ITEM_SPAWN_CHANCE,
  SPECIAL_ITEM_LIFETIME_MS,
  SPEED_BOOST_MULTIPLIER,
  SLOW_MOTION_MULTIPLIER,
  SpecialItemConfig,
  SpecialItemType,
} from '../../components/games/snake/snakeConstants';

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Point {
  x: number;
  y: number;
}

export interface FoodState {
  position: Point;
  emoji: string;
}

export interface SpecialItemState {
  position: Point;
  config: SpecialItemConfig;
  spawnedAt: number;
}

export interface ActiveBuff {
  type: SpecialItemType;
  expiresAt: number;
}

interface EngineState {
  snake: Point[];
  direction: Direction;
  pendingDirection: Direction | null;
  food: FoodState;
  specialItem: SpecialItemState | null;
  score: number;
  lives: number;
  shields: number;
  buffs: ActiveBuff[];
  isGameOver: boolean;
}

const DIRECTION_VECTORS: Record<Direction, Point> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

const OPPOSITES: Record<Direction, Direction> = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
};

function keyOf(p: Point): string {
  return `${p.x},${p.y}`;
}

function randomFruitEmoji(): string {
  return FRUIT_EMOJIS[Math.floor(Math.random() * FRUIT_EMOJIS.length)];
}

function randomSpecialConfig(): SpecialItemConfig {
  return SPECIAL_ITEMS[Math.floor(Math.random() * SPECIAL_ITEMS.length)];
}

function findEmptyCell(occupied: Set<string>): Point | null {
  const free: Point[] = [];
  for (let x = 0; x < GRID_SIZE; x++) {
    for (let y = 0; y < GRID_SIZE; y++) {
      const p = { x, y };
      if (!occupied.has(keyOf(p))) free.push(p);
    }
  }
  if (free.length === 0) return null;
  return free[Math.floor(Math.random() * free.length)];
}

function occupiedCells(snake: Point[], extra?: Point | null): Set<string> {
  const set = new Set(snake.map(keyOf));
  if (extra) set.add(keyOf(extra));
  return set;
}

function spawnFood(snake: Point[], specialItem: SpecialItemState | null): FoodState {
  const occupied = occupiedCells(snake, specialItem?.position ?? null);
  const position = findEmptyCell(occupied) ?? { x: 0, y: 0 };
  return { position, emoji: randomFruitEmoji() };
}

function createInitialEngine(): EngineState {
  const mid = Math.floor(GRID_SIZE / 2);
  const snake: Point[] = [
    { x: mid, y: mid },
    { x: mid - 1, y: mid },
    { x: mid - 2, y: mid },
  ];
  return {
    snake,
    direction: 'right',
    pendingDirection: null,
    food: spawnFood(snake, null),
    specialItem: null,
    score: 0,
    lives: 0,
    shields: 0,
    buffs: [],
    isGameOver: false,
  };
}

function applySpecialEffect(engine: EngineState, config: SpecialItemConfig, now: number) {
  switch (config.type) {
    case 'extra-life':
      engine.lives += 1;
      break;
    case 'shield':
      engine.shields += 1;
      break;
    default: {
      // Süreli etkiler (double-score / speed-boost / slow-motion): varsa süresini yenile.
      const others = engine.buffs.filter((b) => b.type !== config.type);
      engine.buffs = [...others, { type: config.type, expiresAt: now + config.durationMs }];
    }
  }
}

function computeTickMs(buffs: ActiveBuff[], now: number): number {
  let ms = BASE_TICK_MS;
  if (buffs.some((b) => b.type === 'speed-boost' && b.expiresAt > now)) ms *= SPEED_BOOST_MULTIPLIER;
  if (buffs.some((b) => b.type === 'slow-motion' && b.expiresAt > now)) ms *= SLOW_MOTION_MULTIPLIER;
  return Math.max(40, Math.round(ms));
}

function maybeSpawnSpecialItem(engine: EngineState, now: number) {
  if (engine.specialItem) return;
  if (Math.random() >= SPECIAL_ITEM_SPAWN_CHANCE) return;
  const occupied = occupiedCells(engine.snake, engine.food.position);
  const position = findEmptyCell(occupied);
  if (!position) return;
  engine.specialItem = { position, config: randomSpecialConfig(), spawnedAt: now };
}

/** Tek bir oyun adımını (tick) mutasyonla uygular. */
function tick(engine: EngineState, now: number) {
  if (engine.isGameOver) return;

  if (engine.pendingDirection && engine.pendingDirection !== OPPOSITES[engine.direction]) {
    engine.direction = engine.pendingDirection;
  }
  engine.pendingDirection = null;

  const vector = DIRECTION_VECTORS[engine.direction];
  const head = engine.snake[0];
  const newHead: Point = { x: head.x + vector.x, y: head.y + vector.y };

  const hitsWall = newHead.x < 0 || newHead.x >= GRID_SIZE || newHead.y < 0 || newHead.y >= GRID_SIZE;
  const willEatFruit = !hitsWall && newHead.x === engine.food.position.x && newHead.y === engine.food.position.y;
  const bodyToCheck = willEatFruit ? engine.snake : engine.snake.slice(0, -1);
  const hitsSelf = !hitsWall && bodyToCheck.some((seg) => seg.x === newHead.x && seg.y === newHead.y);
  const collision = hitsWall || hitsSelf;

  if (collision) {
    if (engine.shields > 0) {
      engine.shields -= 1;
    } else if (engine.lives > 0) {
      engine.lives -= 1;
    } else {
      engine.isGameOver = true;
    }
    return;
  }

  const willEatSpecial =
    !!engine.specialItem &&
    newHead.x === engine.specialItem.position.x &&
    newHead.y === engine.specialItem.position.y;

  const newSnake = [newHead, ...engine.snake];

  if (willEatFruit) {
    const isDouble = engine.buffs.some((b) => b.type === 'double-score' && b.expiresAt > now);
    engine.score += FRUIT_SCORE * (isDouble ? 2 : 1);
    engine.food = spawnFood(newSnake, engine.specialItem);
    // Meyve yendiğinde büyür — kuyruk atılmaz.
  } else {
    newSnake.pop();
  }

  if (willEatSpecial && engine.specialItem) {
    applySpecialEffect(engine, engine.specialItem.config, now);
    engine.specialItem = null;
  }

  engine.snake = newSnake;
  engine.buffs = engine.buffs.filter((b) => b.expiresAt > now);

  if (engine.specialItem && now - engine.specialItem.spawnedAt > SPECIAL_ITEM_LIFETIME_MS) {
    engine.specialItem = null;
  }

  maybeSpawnSpecialItem(engine, now);
}

export function useSnakeGame() {
  const engineRef = useRef<EngineState>(createInitialEngine());
  const timeoutIdRef = useRef<number | null>(null);
  const loopRef = useRef<() => void>(() => {});
  const [renderVersion, setRenderVersion] = useState(0);

  loopRef.current = () => {
    const now = Date.now();
    tick(engineRef.current, now);
    setRenderVersion((v) => v + 1);
    if (!engineRef.current.isGameOver) {
      const delay = computeTickMs(engineRef.current.buffs, now);
      timeoutIdRef.current = window.setTimeout(() => loopRef.current(), delay);
    }
  };

  useEffect(() => {
    timeoutIdRef.current = window.setTimeout(() => loopRef.current(), BASE_TICK_MS);
    return () => {
      if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDirection = useCallback((next: Direction) => {
    const engine = engineRef.current;
    if (engine.isGameOver) return;
    if (next === OPPOSITES[engine.direction]) return;
    engine.pendingDirection = next;
  }, []);

  const restart = useCallback(() => {
    if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
    engineRef.current = createInitialEngine();
    setRenderVersion((v) => v + 1);
    timeoutIdRef.current = window.setTimeout(() => loopRef.current(), BASE_TICK_MS);
    // NOT: İleride restart burada yeni bir "round başladı" XP/istatistik event'i tetikleyebilir.
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const map: Record<string, Direction> = {
        ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        w: 'up', W: 'up', a: 'left', A: 'left', s: 'down', S: 'down', d: 'right', D: 'right',
      };
      const dir = map[e.key];
      if (dir) {
        e.preventDefault();
        setDirection(dir);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setDirection]);

  // NOT: İleride oyun bittiğinde (isGameOver true olduğunda) burada
  // XP/başarım/istatistik event'i tetiklenebilir. Şu an için implemente edilmedi.

  const engine = engineRef.current;
  void renderVersion; // her tick'te yeniden render tetiklemek için kullanılır

  return {
    gridSize: GRID_SIZE,
    snake: engine.snake,
    direction: engine.direction,
    food: engine.food,
    specialItem: engine.specialItem,
    score: engine.score,
    lives: engine.lives,
    shields: engine.shields,
    buffs: engine.buffs,
    isGameOver: engine.isGameOver,
    setDirection,
    restart,
  };
}
